<template>
	<ion-header>
		<div ref="barRef" class="top-bar">
			<div id="logo" class="logo">
				<img class="logo-full" :src="logoFull" alt="Vurbo.ai" />
				<img class="logo-mark" :src="logoMark" alt="Vurbo.ai" />
			</div>
			<div v-if="channelCode" class="header-code">
				<span>{{ channelCode }}</span>
			</div>
			<div class="nav-right-container">
				<slot name="right"></slot>
			</div>
		</div>
	</ion-header>
</template>
<script lang="ts">
import { defineComponent, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import logoFull from '@shared-assets/images/logos/vurbo-ai-logo.svg'
import logoMark from '@shared-assets/images/logos/logo.svg'

export default defineComponent({
	name: 'NavigationBar',
	props: {
		/**
		 * 頻道代碼，空字串或未傳就不顯示
		 */
		channelCode: {
			type: String,
			default: '',
		},
	},
	setup(props, { expose }) {
		// 頂欄元素
		const barRef = ref<HTMLElement | null>(null)
		// 監聽右側寬度變化的觀察器
		let resizeObserver: ResizeObserver | null = null

		/**
		 * 依實際寬度收合頂欄：先收頻道代碼、還不夠再把 logo 收成圖示
		 * 量測前提是兩側 flex-shrink: 0 且頻道代碼 min-width: max-content，放不下時整列才會溢出
		 * @return {void}
		 */
		const syncCompact = (): void => {
			// 還沒掛載就不量
			const bar = barRef.value
			if (!bar) {
				return
			}

			// 先還原成完整狀態再量，否則量到的是收合後的寬度
			bar.classList.remove('is-compact', 'hide-code')
			// 放得下就結束
			if (bar.scrollWidth <= bar.clientWidth) {
				return
			}

			// 第一段：頻道代碼先讓位
			bar.classList.add('hide-code')
			// 放得下就結束
			if (bar.scrollWidth <= bar.clientWidth) {
				return
			}

			// 第二段：logo 收成只有圖示
			bar.classList.add('is-compact')
		}

		// 頻道代碼變更後等 DOM 更新再重量
		watch(() => props.channelCode, () => nextTick(syncCompact))

		// 掛載後量一次，並監聽視窗與右側寬度變化
		onMounted(() => {
			syncCompact()
			window.addEventListener('resize', syncCompact)
			// 按鈕文字會隨語言改變寬度，右側容器變寬時也要重量
			if (typeof ResizeObserver !== 'undefined' && barRef.value) {
				resizeObserver = new ResizeObserver(syncCompact)
				resizeObserver.observe(barRef.value.querySelector('.nav-right-container') as Element)
			}
		})

		// 卸載時移除監聽
		onBeforeUnmount(() => {
			window.removeEventListener('resize', syncCompact)
			resizeObserver?.disconnect()
		})

		// 對外暴露重量方法
		expose({ syncCompact })

		return {
			barRef,
			logoFull,
			logoMark,
		}
	},
})
</script>
<style lang="scss">
ion-header {
	flex-shrink: 0;
	box-shadow: none !important;
	-webkit-box-shadow: none !important;

	// 頂欄：寬度與最大寬度跟逐字稿卡片、控制列同一組，三者邊界才會對齊
	.top-bar {
		position: relative;
		display: flex;
		justify-content: space-between;
		align-items: center;
		height: var(--header-height);
		width: calc(100% - 32px);
		max-width: var(--content-max-width);
		margin: 0 auto;
		background-color: var(--ui-page);

		// 左右兩側不可壓縮，放不下時整列才會溢出、量得到
		.logo,
		.nav-right-container {
			flex-shrink: 0;
		}

		.logo {
			padding-left: 0;

			img {
				display: block;
			}

			.logo-full {
				width: 96px;
				height: 27.6px;
			}

			// 只有圖示的版本，平常不顯示
			.logo-mark {
				display: none;
				width: 27.6px;
				height: 27.6px;
			}
		}

		// 頻道代碼：不可壓縮到比文字窄，否則溢出量不到
		.header-code {
			flex: 1 1 auto;
			display: flex;
			align-items: center;
			justify-content: center;
			min-width: max-content;
			padding: 0 12px;
			font-size: 14px;
			font-weight: 600;
			letter-spacing: 0.06em;
			color: var(--text-body-01);
			white-space: nowrap;
		}

		.nav-right-container {
			display: flex;
			align-items: center;
			justify-content: flex-end;
			gap: 4px;
		}

		&.is-compact {
			.logo-full {
				display: none;
			}

			.logo-mark {
				display: block;
			}
		}

		// 窄畫面：頻道代碼比 logo 先讓位
		&.hide-code .header-code {
			display: none;
		}
	}
}

// 頂欄右側按鈕，Share 與 Language 共用
.nav-right-button {
	display: flex;
	justify-content: center;
	align-items: center;
	gap: 6px;
	height: 28px;
	padding: 0 8px;
	// 字級設在按鈕上，內層 icon 的 em 才有正確的換算基準
	font-size: 14px;
	font-family: inherit;
	background-color: var(--ui-white);
	border: 1px solid rgba(0, 0, 0, 0.3);
	border-radius: var(--radius-header-button);
	cursor: pointer;
	transition: all 0.3s ease;

	// 白底按鈕三階：白、Grey/Lightest、Grey/Lighter
	&:hover {
		background-color: var(--ui-grey-lightest);
		border-color: #ccc;
	}

	// 按住與開啟中維持被按住的樣子，外框與底色同色
	&:active,
	&.is-open {
		background-color: var(--ui-grey-lighter);
		border-color: var(--ui-grey-lighter);
	}

	.meet-title-text {
		font-size: 14px;
		font-weight: 700;
		line-height: 1;
		color: var(--text-body-01);
		white-space: nowrap;
	}

	> .nav-icon {
		color: var(--text-body-01);
	}
}

// 內嵌 icon 的外框，svg 填滿它，顏色跟著文字走
.nav-icon {
	display: inline-flex;
	flex-shrink: 0;

	svg {
		display: block;
		width: 100%;
		height: 100%;
	}
}

// share icon 跟著所在位置的字級縮放
.nav-icon.icon-share {
	width: 1.15em;
	height: 1.15em;
}

// globe icon 墨跡填滿整個框，寫死尺寸
.nav-icon.icon-globe {
	width: 13.34px;
	height: 13.34px;
}
</style>
